import parse from "html-react-parser"
import { marked, Renderer } from "marked"

import { replacePlaceholders, LingoReplacement } from "../providers/translations"

import { useLocalization } from "./use-localization"

/** Only renders the attribute if it isn't null or empty */
const htmlAttr = (name: string, value?: string | null) =>
  value != null && value.length > 0 ? `${name}="${value}"` : ""

/**
 * Adds back underlines to markdown links, since we can't replace them with our
 * `TextAnchor` component easily, we have to fake them until we can write a
 * proper solution for this. :(
 */
const linkStyles = 'style="text-decoration: underline;"'

/**
 * We need a custom link renderer since the default one escapes our replacement
 * markers ("{0}") which breaks our replacement functions.
 */
const CustomLink: typeof Renderer.prototype.link = (href, title, text) =>
  `<a ${htmlAttr("href", href)} ${htmlAttr("title", title)} ${linkStyles}>${
    text ?? ""
  }</a>`

const BlankLink: typeof Renderer.prototype.link = (href, title, text) =>
  `<a ${htmlAttr("href", href)} ${htmlAttr(
    "title",
    title,
  )} ${linkStyles} rel="noopener noreferrer" target="_blank">${text ?? ""}</a>`

export type CustomOptions = {
  targetBlank?: boolean
  textKey?: false
  replacements?: LingoReplacement[]
} & marked.MarkedOptions

const getOptions = (options: CustomOptions = {}) => {
  const renderer = new Renderer()

  renderer.link = options.targetBlank !== true ? CustomLink : BlankLink

  options.renderer = renderer

  return options
}

/**
 *  Hook for rendering markdown
 *
 *  Does not sanitize the output.
 */
export const useMarkdown = () => {
  const { text } = useLocalization()

  // Make sure any new functions in here use mdString, otherwise make sure
  // they handle the options properly without it.
  const mdString = (source: string, options?: CustomOptions) => {
    options = getOptions(options)

    if (options.textKey !== false) {
      source = text(source)
    }

    source = marked(source, options)

    return replacePlaceholders(source, options.replacements)
  }

  const mdElement = (source: string, options?: CustomOptions) => {
    const result = mdString(source, options)
    let elements = typeof result === "string" ? parse(result) : result

    if (Array.isArray(elements)) {
      elements = elements.filter((el) => (el as any) !== "\n")
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{elements}</>
  }

  return {
    mdElement,
    mdString,
  }
}
